/**
 * Photo Gallery Component
 *
 * Provides a fullscreen lightbox gallery for viewing property photos.
 * Features:
 * - Navigate between photos with prev/next buttons
 * - Keyboard navigation (left/right arrows, escape to close)
 * - Click outside to close
 * - Photo counter display
 *
 * Registered globally for Turbo Frame compatibility.
 */

export function photoGallery() {
  return {
    // Get photos array from store
    get photos() {
      return Alpine.store('modals').photoGalleryPhotos || []
    },

    // Get/set current index
    get currentIndex() {
      return Alpine.store('modals').photoGalleryCurrentIndex
    },
    set currentIndex(value) {
      Alpine.store('modals').photoGalleryCurrentIndex = value
    },

    // Get current photo
    get currentPhoto() {
      return this.photos[this.currentIndex] || null
    },

    // Check if there's a previous photo
    get hasPrevious() {
      return this.currentIndex > 0
    },

    // Check if there's a next photo
    get hasNext() {
      return this.currentIndex < this.photos.length - 1
    },

    // Navigate to previous photo
    previous() {
      if (this.hasPrevious) {
        this.currentIndex--
      }
    },

    // Navigate to next photo
    next() {
      if (this.hasNext) {
        this.currentIndex++
      }
    },

    // Go to a specific photo
    goTo(index) {
      if (index >= 0 && index < this.photos.length) {
        this.currentIndex = index
      }
    },

    // Close the gallery
    close() {
      Alpine.store('modals').isPhotoGalleryOpen = false
    },

    // Handle keyboard navigation (only when gallery is open)
    handleKeydown(event) {
      if (!Alpine.store('modals').isPhotoGalleryOpen) return

      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault()
          this.previous()
          break
        case 'ArrowRight':
          event.preventDefault()
          this.next()
          break
        case 'Escape':
          event.preventDefault()
          this.close()
          break
      }
    },

    // Initialize keyboard listeners when modal opens
    init() {
      this._keydownHandler = this.handleKeydown.bind(this)
      document.addEventListener('keydown', this._keydownHandler)
    },

    // Clean up keyboard listeners when modal closes
    destroy() {
      if (this._keydownHandler) {
        document.removeEventListener('keydown', this._keydownHandler)
      }
    }
  }
}
